import { apiClient } from '@/lib/apiClient';
import type { QueryOptions } from '@/lib/reactQuery';
import type { DateString, Uuid } from '@/types/api';
import type { Appointment, StudentRequestBody } from '@/types/scheduling';
import { useQuery } from '@tanstack/react-query';

export interface GetCalendarPeriodResponseData {
  available_days: { date: DateString; can_book: boolean }[];
  appointments: Appointment[];
}

interface GetCalendarPeriodParams {
  students: StudentRequestBody[];
  startDate: DateString;
  endDate: DateString;
  reschedulingAppointmentId?: Uuid;
  /**
   * The browser's IANA timezone, e.g. "America/Phoenix".
   *
   * `startDate`/`endDate` are built with date-fns (`startOfMonth`/`endOfMonth`) in this zone
   * and then serialised with `toISOString()`, which discards the offset. The backend needs
   * the zone name to group availability into this guardian's own calendar days; otherwise it
   * falls back to the center's zone and the grid drifts by a day around DST — and stays
   * drifted for a guardian whose zone has no DST at all.
   */
  timeZone?: string;
}

export const getCalendarPeriod = async ({ ...params }: GetCalendarPeriodParams) => {
  const response = await apiClient.post<GetCalendarPeriodResponseData>('/v1/scheduling/get-calendar-period', { ...params });
  return response.data;
};

interface UseGetCalendarPeriodOptions {
  params: GetCalendarPeriodParams;
  options?: QueryOptions<GetCalendarPeriodResponseData>;
}

export const GET_CALENDAR_PERIOD_QUERY_KEY = 'getCalendarPeriod';

export const useGetCalendarPeriod = ({ params, options }: UseGetCalendarPeriodOptions) => {
  return useQuery({
    // timeZone is part of the key: it changes how the backend groups days, so a cached
    // result from another zone is not interchangeable.
    queryKey: [GET_CALENDAR_PERIOD_QUERY_KEY, params.startDate, params.endDate, params.students, params.timeZone ?? null],
    queryFn: () => getCalendarPeriod(params),
    ...options,
  });
};
